import { GOALS } from '../lib/goals';
import { COMPOUNDS, type Compound } from '../lib/compounds';
import { logSearchMiss, trackEvent } from '../lib/analytics';

type ChipFilter = { id: string | null; label: string };

const CHIPS: ChipFilter[] = [
  { id: null,            label: 'All' },
  ...GOALS.map((g) => ({ id: 'goal:' + g.id, label: `${g.icon} ${g.label}` })),
];

let _state = { q: '', filter: null as string | null };

export function renderHome() {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <section class="hero">
      <img src="/icon.jpg" alt="MyStack" style="width:240px;height:240px;display:block;margin:0 auto 14px;" />
      <p>Build your supplement stack from the same research a clinician would read — paraphrased into plain English with every claim cited. No paywall. Vendor links may pay us a small cut.</p>
      <div class="cta">
        <a class="btn btn-primary" href="#compounds">Browse compounds</a>
        <a class="btn" href="/app-release.apk" download>📱 Download Android APK</a>
        <a class="btn" href="/login">Sign in to save stacks</a>
      </div>
      <p style="font-size:12px;color:var(--dim);margin-top:8px;">APK installs outside the Play Store — Android will offer to scan it with Play Protect or skip.</p>
    </section>

    <h2 class="section-title" id="compounds">All compounds · <span id="compound-count">${COMPOUNDS.length}</span></h2>

    <div class="chips-row" id="chips-row">
      ${CHIPS.map((ch) => chipHtml(ch, ch.id === _state.filter)).join('')}
    </div>

    <div class="search-wrap" style="margin: 10px 0 14px;">
      <input id="search-input" type="search" placeholder="Search by name, alias, goal…" value="${esc(_state.q)}"
             style="width:100%;padding:10px 36px 10px 12px;background:var(--bg-3);border:1px solid var(--line);border-radius:8px;color:var(--ink);font-size:14px;" />
      <button class="search-clear" id="search-clear" style="display:${_state.q ? 'block' : 'none'};" aria-label="Clear search">×</button>
    </div>

    <div class="compound-grid" id="compound-grid">
      ${renderResults()}
    </div>
  `;
  wireEvents();
}

function chipHtml(ch: ChipFilter, selected: boolean): string {
  const bg = selected ? 'var(--acc)' : 'var(--bg-3)';
  const fg = selected ? '#000' : 'var(--ink)';
  const bd = selected ? 'var(--acc)' : 'var(--line)';
  return `<button class="chip" data-id="${esc(ch.id ?? '__all__')}"
            style="display:inline-block;padding:6px 12px;margin:0 6px 6px 0;background:${bg};color:${fg};
                   border:1px solid ${bd};border-radius:14px;font-size:12px;font-weight:700;cursor:pointer;">
            ${esc(ch.label)}
          </button>`;
}

function applyFilter(items: Compound[]): Compound[] {
  const q = _state.q.trim().toLowerCase();
  return items.filter((c) => {
    if (_state.filter && _state.filter.startsWith('goal:')) {
      const g = _state.filter.substring(5);
      if (!c.goals.includes(g)) return false;
    }
    if (!q) return true;
    if (c.display_name.toLowerCase().includes(q)) return true;
    if (c.scientific_name.toLowerCase().includes(q)) return true;
    if (c.aliases.some((a) => a.toLowerCase().includes(q))) return true;
    if (c.goals.some((g) => g.toLowerCase().includes(q))) return true;
    return false;
  });
}

function renderResults(): string {
  const items = applyFilter(COMPOUNDS);
  if (items.length === 0) {
    return `<div style="padding:20px;color:var(--dim);grid-column:1/-1;">No compounds match. Try a shorter search or "All" chip.</div>`;
  }
  return items.map(compoundCard).join('');
}

let _searchMissTimer: number | null = null;
function refresh() {
  const grid = document.getElementById('compound-grid');
  if (grid) grid.innerHTML = renderResults();
  // After the debounce window, if the query still returns zero, log it as a
  // catalog gap (Tier 0 — no consent needed, server-side miss log).
  if (_searchMissTimer) window.clearTimeout(_searchMissTimer);
  if (_state.q.trim().length >= 3) {
    const queryAtSchedule = _state.q;
    _searchMissTimer = window.setTimeout(() => {
      if (queryAtSchedule === _state.q && applyFilter(COMPOUNDS).length === 0) {
        logSearchMiss(queryAtSchedule);
      }
    }, 1500) as unknown as number;
  }
  const count = document.getElementById('compound-count');
  if (count) count.textContent = `${applyFilter(COMPOUNDS).length}`;
  const clear = document.getElementById('search-clear');
  if (clear) (clear as HTMLElement).style.display = _state.q ? 'block' : 'none';
  // Repaint chips to reflect selected state.
  const chipsRow = document.getElementById('chips-row');
  if (chipsRow) {
    chipsRow.innerHTML = CHIPS.map((ch) => chipHtml(ch, ch.id === _state.filter)).join('');
    wireChips();
  }
}

function wireChips() {
  document.querySelectorAll('#chips-row .chip').forEach((b) => {
    b.addEventListener('click', () => {
      const id = (b as HTMLElement).dataset.id;
      _state.filter = id === '__all__' ? null : (id ?? null);
      if (_state.filter && _state.filter.startsWith('goal:')) {
        trackEvent('goal_chip_tap', { goal: _state.filter.substring(5) });
      }
      refresh();
    });
  });
}

function wireEvents() {
  wireChips();
  const input = document.getElementById('search-input') as HTMLInputElement | null;
  if (input) {
    input.addEventListener('input', () => {
      _state.q = input.value;
      refresh();
    });
  }
  const clear = document.getElementById('search-clear');
  if (clear) {
    clear.addEventListener('click', () => {
      _state.q = '';
      if (input) input.value = '';
      refresh();
    });
  }
}

function highlight(text: string, q: string): string {
  if (!q) return esc(text);
  const lower = text.toLowerCase();
  const qLow = q.toLowerCase();
  let out = '';
  let cursor = 0;
  while (true) {
    const i = lower.indexOf(qLow, cursor);
    if (i < 0) break;
    if (i > cursor) out += esc(text.substring(cursor, i));
    out += `<span class="shi">${esc(text.substring(i, i + q.length))}</span>`;
    cursor = i + q.length;
  }
  if (cursor < text.length) out += esc(text.substring(cursor));
  return out;
}

function teaserFor(desc: string, q: string): string {
  if (desc.length <= 140) return desc;
  if (!q) return desc.slice(0, 140) + '…';
  const idx = desc.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0 || idx < 100) return desc.slice(0, 140) + '…';
  const start = Math.max(0, idx - 40);
  const end = Math.min(desc.length, start + 140);
  const prefix = start === 0 ? '' : '…';
  const suffix = end === desc.length ? '' : '…';
  return prefix + desc.slice(start, end) + suffix;
}

export function compoundCard(c: Compound) {
  const stars = '★'.repeat(c.evidence_stars) + '<span class="stars-empty">' + '★'.repeat(5 - c.evidence_stars) + '</span>';
  const teaser = teaserFor(c.description, _state.q);
  const q = _state.q.trim();
  return `
    <a class="compound-card" href="/c/${esc(c.id)}">
      <div class="name">${highlight(c.display_name, q)}</div>
      <div class="sci">${highlight(c.scientific_name, q)}</div>
      <div class="desc">${highlight(teaser, q)}</div>
      <div class="meta"><span class="stars">${stars}</span> &nbsp; evidence</div>
      <div class="tags">${c.goals.map((g) => `<span class="tag">${esc(g)}</span>`).join('')}</div>
    </a>
  `;
}

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
